import {
  ActivityLocationDraftMeetingProvider,
  ActivityLocation as ApiActivityLocation,
  ActivityLocationDraft as ApiActivityLocationDraft,
  ActivityLocationKind as ApiActivityLocationKind
} from '@api/models';

import type { ActivityStepLocation } from '@ui/components/features/locationLibrary';
import { ApiClient } from '@website/lib/api';

import { commonLocationFields } from './locationBody';

/**
 * Activity-level locations. An activity owns a SET of bookable locations; slots
 * pick among them, and a booking stores the chosen activity_location id and resolves
 * its display LIVE (only a past/frozen booking reads its own snapshot). This is the
 * website-facing projection of the generated `ActivityLocation` so components and
 * hooks never depend on the orval-generated enum identities directly.
 *
 * The API `kind` is the explicit 3-value kind `venue | at_home | online` (same as
 * the wizard UI), so it passes straight through with no radius-based guessing.
 */
export type ActivityLocationKind = 'venue' | 'at_home' | 'online';
export type MeetingProvider = 'toptoptime' | 'byo' | 'manual';

export interface ActivityLocation {
  id: string;
  activityId: string;
  kind: ActivityLocationKind;
  label?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  /** at_home travel radius in meters */
  radius?: number;
  meetingProvider?: MeetingProvider;
  meetingUrl?: string;
  isActive?: boolean;
  /** Count of upcoming (future, non-cancelled) bookings frozen onto this location. */
  upcomingBookings: number;
  /** Whether a live time slot still references this location (so it can't be removed). */
  usedByLiveSlot?: boolean;
}

/**
 * The API kind is the explicit 3-value kind (`venue | at_home | online`), so it
 * maps straight onto the UI kind with no radius guessing.
 */
const fromApiKind = (apiKind: ApiActivityLocationKind): ActivityLocationKind =>
  apiKind as unknown as ActivityLocationKind;

const toApiKind = (kind: ActivityLocationKind): ApiActivityLocationKind => kind as unknown as ApiActivityLocationKind;

const toApiProvider = <T>(provider: MeetingProvider | undefined): T | undefined =>
  provider ? (provider as unknown as T) : undefined;

/** Map a generated activity-location row onto the website-facing shape. */
const fromApi = (l: ApiActivityLocation, upcomingBookings = 0): ActivityLocation => ({
  id: l.id,
  activityId: l.activityId,
  kind: fromApiKind(l.kind),
  label: l.label,
  addressLine1: l.addressLine1,
  addressLine2: l.addressLine2,
  city: l.city,
  state: l.state,
  zipCode: l.zipCode,
  country: l.country,
  latitude: l.latitude,
  longitude: l.longitude,
  radius: l.radius,
  meetingProvider: l.meetingProvider as unknown as MeetingProvider | undefined,
  meetingUrl: l.meetingUrl,
  isActive: l.isActive,
  usedByLiveSlot: l.usedByLiveSlot,
  upcomingBookings
});

/**
 * List an activity's active locations, each carrying its upcoming-booking count.
 * Locations are part of the ACTIVITY response (there is no separate list endpoint);
 * the owner sees the per-location upcoming-booking count, others get 0.
 */
export const getActivityLocations = async (activityId: string): Promise<ActivityLocation[]> => {
  const activity = await ApiClient.getActivityById(activityId);
  return (activity.locations ?? [])
    .filter((l) => l.isActive !== false)
    .map((l) => fromApi(l, l.upcomingBookingsCount ?? 0));
};

/**
 * Map the wizard's working DRAFT (UI locations) to the API draft set sent with the
 * activity update. The server reconciles it (create/update/delete + freeze-past on
 * edited/removed locations). A real existing id is forwarded; a locally-added
 * location (temp `tmp-…` id) is sent without an id so the server creates it.
 */
export const toActivityLocationDrafts = (locations: ActivityStepLocation[]): ApiActivityLocationDraft[] =>
  locations.map((l) => {
    const isOnline = l.kind === 'online';
    return {
      ...(l.id && !l.id.startsWith('tmp-') ? { id: l.id } : {}),
      kind: toApiKind(l.kind),
      ...(l.label ? { label: l.label } : {}),
      // Online locations carry no address / geo; venue & at_home carry no meeting url.
      ...commonLocationFields(
        isOnline
          ? { meetingUrl: l.meetingUrl }
          : {
              addressLine1: l.addressLine1,
              addressLine2: l.addressLine2,
              city: l.city,
              state: l.state,
              zipCode: l.zipCode,
              country: l.country,
              latitude: l.latitude,
              longitude: l.longitude,
              radius: l.radius
            }
      ),
      ...(isOnline && l.meetingProvider
        ? { meetingProvider: toApiProvider<ActivityLocationDraftMeetingProvider>(l.meetingProvider) }
        : {})
    };
  });
